import { Injectable } from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { GroupType } from 'src/AngularJS/Models/group-type.model';
import { GroupDTO } from 'src/app/reusables/assign-tool/angular-assign-tool/angular-assign-tool.models';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { MetricDefinition } from 'src/shared/models/pipeline-config/metric-definition.models';
import { CacheState, CachedRequestPromise } from 'src/shared/models/pipeline-config/pipeline-configuration.models';
import { ReportingSetDefinition } from 'src/shared/models/pipeline-config/reporting-sets.models';
import { ComparisonType } from 'src/shared/models/pipeline-config/mongo-enums.model';

export interface GroupTypes {
    emp: GroupType[];
    rec: GroupType[];
}

export interface SaveMetricResponse {
    Validation: MetricDefinitionValidation;
    MetricID: number;
}

export const enum MetricDefinitionValidation {
    Success = 0,
    MissingName = 1,
    MissingAbv = 2,
    MissingCategory = 3,
    MissingFormatType = 4,
    MissingTimeFormat = 5,
    MissingComponentType = 6,
    BadFormula = 7,
    MissingDefinitionForId = 8,
}

export interface Permissions {
    CreateAccess: boolean;
    ReadAccess: boolean;
    WriteAccess: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CxoneManageMetricsService {
    public popoverObj: NgbPopover;
    public metricLocalization: Map<string, string>;
    private groupTypes: GroupTypes = null;
    private reportingSetDefinitionCache = new CachedRequestPromise<ReportingSetDefinition[]>();
    private distinctValueCache: Map<string, Map<string, Map<string, string[]>>> = new Map<string, Map<string, Map<string, string[]>>>();
    public filterFieldsModalValidations: Boolean[] = [];
    public permissions: Permissions = null;



    constructor(
        private cvApiService: CVApiService,
        private cvLocalizationService:CVLocalizationService
    ) { 
        this.getLocalization();
    }

    public async getMetricDefinitions() {
        try {
            let response = await this.cvApiService.get<MetricDefinition[]>("/api/v1/pipeline-config/metric-definitions");
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load metric definitions.');
        }
    }

    /**
     * Saves the provided Metric Definition object.
     * The endpoint differentiates between an edited or a new metric based on the MetricID value.
     * @param metricDefinition Metric Definition object to save.
     */
    public async postSaveMetricDefinition(metricDefinition: MetricDefinition) {
        try {
            // ClearView WebPortal/AppCode/Api/Controllers/Pipeline/PipelineDataController.cs
            let response = await this.cvApiService.post<SaveMetricResponse>("/api/v2/pipeline/metric-definition/save-definition", metricDefinition);
            // 200 on edited, 201 on created:
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else
                throw response;
        }
        catch (error) {
            console.error('Failed to load metric definitions.');
        }
    }

    public async deactivateMetricDefinition(metricID){
        try{
            let response = await this.cvApiService.delete<null>("/api/v2/pipeline/metric-definition/" + metricID);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch(error){
            console.error('Failed to deactivate metric definitions.');
        }
    }

    public async getLocalization(){
        this.metricLocalization = await this.cvLocalizationService.GetSection("Metrics");

    }

    public async getGroupTypes() {
        if (this.groupTypes)
            return JSON.parse(JSON.stringify(this.groupTypes));

        try{
            let response = await this.cvApiService.get<GroupTypes>(`/api/v2/pipeline/metric-definition/group-types`);
            if (response.status_code == 200) {
                this.groupTypes = response.data;
                return JSON.parse(JSON.stringify(response.data));
            } 
            else 
                throw response;
        }
        catch(error){
            console.error('Failed to get group types.');
        }
    }

    public async getRecGroups(): Promise<{ pointsOfContact: Map<number, GroupDTO>, skills: Map<number, GroupDTO>, campaigns: Map<number, GroupDTO>, companies: Map<number, GroupDTO> }> {
        const url = "/api/v1/rec_groups";
        let pointsOfContact: Map<number, GroupDTO> = new Map<number, GroupDTO>();
        let skills: Map<number, GroupDTO> = new Map<number, GroupDTO>();
        let campaigns: Map<number, GroupDTO> = new Map<number, GroupDTO>();
        let companies: Map<number, GroupDTO> = new Map<number, GroupDTO>();
        try {
            let promises: Promise<any>[] = [];
            [1, 2, 3, 4].forEach((i)=>{
                promises.push(this.cvApiService.get<CvAssignmentGroupItemDTO[]>(`${url}/${i}`).then(
                    (value) => { if (value.status_code == 200) return value.data; throw `Failed to load level ${i} rec groups.`; },
                    (reason) => { throw `Failed to load level ${i} rec groups; ${reason}.`; }
                ));
            });

            let response: CvAssignmentGroupItemDTO[][] = await Promise.all(promises).then(null, (reason) => { throw reason; });

            // Points of Contact
            response[0].forEach((e) => {
                if (!pointsOfContact.has(e.groupDTO.GroupID))
                    pointsOfContact.set(e.groupDTO.GroupID, e.groupDTO);
            });

            // Skills
            response[1].forEach((e) => {
                if (!skills.has(e.groupDTO.GroupID))
                    skills.set(e.groupDTO.GroupID, e.groupDTO);
            });

            // Campaigns
            response[2].forEach((e) => {
                if (!campaigns.has(e.groupDTO.GroupID))
                    campaigns.set(e.groupDTO.GroupID, e.groupDTO);
            });

            // Companies
            response[3].forEach((e) => {
                if (!companies.has(e.groupDTO.GroupID))
                    companies.set(e.groupDTO.GroupID, e.groupDTO);
            });
        }
        catch (error) {
            pointsOfContact = null;
            skills = null;
            campaigns = null;
            console.error(error);
        }

        return {
            pointsOfContact: pointsOfContact,
            skills: skills,
            campaigns: campaigns,
            companies: companies,
        }
    }

    public async getReportingSets(): Promise<ReportingSetDefinition[]> {
        if (this.reportingSetDefinitionCache.state == CacheState.Unloaded)
            this.reportingSetDefinitionCache.setPromise(this.cvApiService.get<ReportingSetDefinition[]>("/api/v1/pipeline-config/reporting-set-definitions"));

        try {
            let response = await this.reportingSetDefinitionCache.promise;
            if (response.status_code == 200)
                return JSON.parse(JSON.stringify(response.data));
            throw response;
        } catch (ex) {
            console.error("Could not retrieve reporting set definitions:", ex);
            this.reportingSetDefinitionCache.clear();
        }
    }

    public async getDistinctValuedDictForDataField(reportingSet: string, dataFields: string[], dateField: string, limit?: number): Promise<string[]> {
        let reportingSetCache = this.distinctValueCache.get(reportingSet) ?? new Map<string, Map<string, string[]>>();
        this.distinctValueCache.set(reportingSet, reportingSetCache);
        let dataFieldsJSON = JSON.stringify(dataFields);
    
        let dataFieldCache = reportingSetCache.get(dataFieldsJSON) ?? new Map<string, string[]>();
        reportingSetCache.set(dataFieldsJSON, dataFieldCache);
    
        let distinctValues: string[] = dataFieldCache.get(dateField);
        if (distinctValues)
          return distinctValues;
    
        let encodedReportingSet = encodeURIComponent(reportingSet);
        let encodedDataField = encodeURIComponent(dataFieldsJSON);
        let encodedDateField = encodeURIComponent(dateField ?? "");
         let dateFieldParameter = encodedDateField && `?dateField=${encodedDateField}` || "";
        let limitQueryParameter = "";
        if (limit !== undefined)
          limitQueryParameter = (dateFieldParameter ? "&" : "?") + `limit=${limit}`;
    
        try {
          // ClearView WebPortal/AppCode/Api/Controllers/Pipeline/PipelineDataController.cs
          let apiUrl = `/api/v2/reporting-set/dictionary/${encodedReportingSet}/distinct/${dateField}/${10}`
          let response = await this.cvApiService.post<any[]>(apiUrl, dataFields);
          if (response.status_code == 200) {
            return response.data;
          } else {
            throw response;
          }
        } catch (ex) {
          console.warn(`Could not get distinct values for reportingSet="${reportingSet}" dataField="${dataFields}" dateField="${dateField}" limit="${limit}"`, ex);
          return [];
        }
      }

    // In display order, not ComparisonType order
    // This is an array of arrays in order to guarantee ordering for <select ng-options>
    public numericComparisonTypes: [ComparisonType, string][] = [
        [ComparisonType.Equal, "="],
        [ComparisonType.NotEqual, "\u2260"],
        [ComparisonType.LessThan, "<"],
        [ComparisonType.GreaterThan, ">"],
        [ComparisonType.GreaterThanOrEqual, "\u2265"],
        [ComparisonType.LessThanOrEqual, "\u2264"],
    ];
  
    // Strings have Equal, Not Equal, Contain, StartWith, and EndWith.
    public stringComparisonTypes: [ComparisonType, string][] = [
        ...this.numericComparisonTypes.slice(0, 2),
        [ComparisonType.Contain, "contains"],
        [ComparisonType.StartWith, "starts with"],
        [ComparisonType.EndWith, "ends with"],
    ];
  
    public getStringComparisonTypes(localizations){
        let stringComparisonTypes: [ComparisonType, string][] = [
            ...this.numericComparisonTypes.slice(0, 2),
            [ComparisonType.Contain, localizations.get("Contains")],
            [ComparisonType.StartWith, localizations.get("StartWith")],
            [ComparisonType.EndWith, localizations.get("EndWith")],
        ];

        return stringComparisonTypes;
    }

    public getDisplayComparisonTypes(key, localizations: Map<string, string>){
        let displayTypes = {
            [ComparisonType.Equal]: "=",
            [ComparisonType.NotEqual]: "\u2260",
            [ComparisonType.GreaterThan]: ">",
            [ComparisonType.GreaterThanOrEqual]: "\u2265",
            [ComparisonType.LessThan]: "<",
            [ComparisonType.LessThanOrEqual]: "\u2264",
            // The localized values for these are instead used in MetricComponentPreviewComponent.formatCompValue():
            [ComparisonType.Contain]: localizations.get("Contains"),
            [ComparisonType.StartWith]: localizations.get("StartWith"),
            [ComparisonType.EndWith]: localizations.get("EndWith"),
        };

        return displayTypes[key];
    }

    public async validateFormula(formula:string){
        if(formula == null){
            return false;
        }
        
        try {
            let postData = { formula };
            let response = await this.cvApiService.post<formulaValidationFlags>("/api/v2/pipeline/metric-definition/validate-formula-metrics", postData);
            if(response.status_code == 200) 
                return response.data;
            else
                throw response
        }
        catch (ex) {
            console.error("Could not validate formula.");
        }
    }

    public async getManageMetricsPermissions(): Promise<Permissions> {
        try {
            let response = await this.cvApiService.get<Permissions>("/api/v3/cxone/getManageMetricsPermissions/");
            if (response.status_code < 200 || response.status_code >= 300) {
                throw response;
            }
            else {
                this.permissions = response.data;
                return response.data;
            }
        }
        catch (ex) {
            console.error("Failed to get permissions.");
        }
    }
}

export interface GroupAssignment {
    companyID: number;
    company: string;
    campaignID?: number;
    campaign?: string;
    skillID?: number;
    skill?: string;
    pointOfContactID?: number;
    pointOfContact?: string;
}

export interface CvAssignmentGroupItemDTO {
    groupDTO: GroupDTO;
    parentPath: string[];
}

interface formulaValidationFlags {
    formulaInvalid: boolean;
    formulaInvalidMetric: boolean;
    formulaSpecialCharacter: boolean;
}
